import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import theme from './Theme';
import { initMixpanel } from "./utils/mixpanel";
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';

import * as Sentry from "@sentry/react";

// Initialize React Query Client with 10-minute stale time
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      gcTime: 24 * 60 * 60 * 1000, // 24 hours (previously cacheTime)
    },
  },
});

window.__TANSTACK_QUERY_CLIENT__ = queryClient;

// Create localStorage persister
const persister = createSyncStoragePersister({
  storage: window.localStorage,
  key: 'GURUNOW_REACT_QUERY_CACHE',
});

// Security: never persist auth/PII queries to localStorage. The `user` query holds
// the user's email + profile; persisting it leaves PII readable by any XSS and
// lingering on shared machines. Keep React Query's default (only persist
// successful queries) and additionally drop sensitive keys.
const shouldDehydrateQuery = (query) => {
  if (query.state.status !== 'success') return false;
  const key = query.queryKey?.[0];
  return key !== 'user';
};

// Drop persisted cache older than 1 hour and bust it whenever a new build ships,
// so stale snapshots don't outlive a deploy.
const PERSIST_MAX_AGE = 60 * 60 * 1000; // 1 hour
const PERSIST_BUSTER = import.meta.env.VITE_GIT_COMMIT ?? 'dev';

if (import.meta.env.VITE_ENVIRONMENT !== 'local') {
  // Sentry error monitoring: legitimate interest — no consent required for crash reporting.
  // Performance profiling is disabled until analytics consent is given.
  Sentry.init({
    dsn: 'https://3655e8895c390a1a94aefe4707a2aa82@o4509479819083776.ingest.de.sentry.io/4509480132280400',
    sendDefaultPii: false,
    integrations: [],
    tracesSampleRate: 0,
    profilesSampleRate: 0,
    environment: import.meta.env.VITE_ENVIRONMENT || 'dev',
  });
}

// Mixpanel and Sentry performance profiling only initialise after analytics consent.
// The gn:analyticsConsent event is dispatched by cookieConsent.js onConsent/onChange.
window.addEventListener('gn:analyticsConsent', () => {
  if (import.meta.env.VITE_ENVIRONMENT === 'local') return;

  if (import.meta.env.VITE_MIXPANEL_TOKEN) {
    initMixpanel();
  }

  // Upgrade Sentry to include performance profiling now that user has consented
  Sentry.init({
    dsn: 'https://3655e8895c390a1a94aefe4707a2aa82@o4509479819083776.ingest.de.sentry.io/4509480132280400',
    sendDefaultPii: true,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.browserProfilingIntegration(),
    ],
    tracesSampleRate: 1.0,
    tracePropagationTargets: ['localhost', 'gurunow.ai'],
    profilesSampleRate: 1.0,
    environment: import.meta.env.VITE_ENVIRONMENT || 'dev',
  });
});

const root = ReactDOM.createRoot(document.getElementById('root'));

root.render(
  <React.StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: PERSIST_MAX_AGE,
        buster: PERSIST_BUSTER,
        dehydrateOptions: { shouldDehydrateQuery },
      }}
    >
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <App />
      </ThemeProvider>
    </PersistQueryClientProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
